import { IDashboard } from '@src/types/SiteMetaUI';
import { useCallback, useEffect, useState } from 'react';
import { Layout, Layouts, Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import Spinner from '@components/_elements/Spinner/Spinner';
import { dashboardSx } from '@components/_shared/sxStyles/Dashboard/dashboardStyles';
import FormattedAlias from '@components/_shared/Translations/FormattedAlias';
import DashboardCell from '@components/Home/Dashboard/DashboardCell';
import ResetConfirmationDialog from '@components/Home/Dashboard/ResetConfirmationDialog';
import useMakePageScrollable from '@hooks/useMakePageScrollable';
import RestoreIcon from '@mui/icons-material/Restore';
import { Box, Button } from '@mui/material';
import { SiteMeta } from '@src/types/SiteMeta';
import { notify, notifyError } from '@store/actionCreators/notifications';
import { AppDefaultStateProps } from '@store/reducers';
import { getFromLS, saveToLS } from '@utils/index_ts';
import { connect } from 'react-redux';

const ResponsiveReactGridLayout = WidthProvider(Responsive);

interface DashboardsProps {
  siteMeta: SiteMeta;
  notify: (message: string) => void;
  notifyError: (message: string) => void;
}

function Dashboards({ siteMeta, notify, notifyError }: DashboardsProps) {
  useMakePageScrollable();
  const [isLoading, setIsLoading] = useState(true);
  const [currentDashboard, setCurrentDashboard] = useState<IDashboard>();
  const [layouts, setLayouts] = useState<Layouts>();
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const loadDashboard = useCallback(() => {
    setIsLoading(true);
    const lsCurrentDashboard = getFromLS<IDashboard>(
      'fractal-ems-dashboards-layouts',
      'currentDashboard',
    );
    const initialDashboard = lsCurrentDashboard || siteMeta.ui.dashboards?.[0];
    const lsLayouts = getFromLS<Layouts>(
      'fractal-ems-dashboards-layouts',
      initialDashboard?.id || '0',
    );
    const currentLayout = lsLayouts || initialDashboard?.layouts || {};
    setCurrentDashboard(initialDashboard);
    setLayouts(currentLayout);
    setIsLoading(false);
  }, [siteMeta.ui.dashboards]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleResetClick = useCallback(() => {
    setResetDialogOpen(true);
  }, []);

  const handleResetCancel = useCallback(() => {
    setResetDialogOpen(false);
  }, []);

  const handleResetConfirm = useCallback(() => {
    if (!currentDashboard) {
      notifyError('No dashboard selected');
      setResetDialogOpen(false);
      return;
    }

    try {
      // Find the default dashboard from siteMeta that matches the current dashboard id
      const defaultDashboard = siteMeta.ui.dashboards?.find(
        (dashboard) => dashboard.id === currentDashboard.id,
      );

      if (!defaultDashboard || !defaultDashboard.layouts) {
        notifyError('Unable to find default layout configuration');
        setResetDialogOpen(false);
        return;
      }

      // Reset the layout for the current dashboard
      saveToLS(
        'fractal-ems-dashboards-layouts',
        currentDashboard.id,
        defaultDashboard.layouts,
      );

      // Update the state
      setLayouts(defaultDashboard.layouts);

      // Show success notification
      notify('Dashboard layout reset to defaults');
    } catch {
      notifyError('Failed to reset dashboard. Please try again.');
    } finally {
      setResetDialogOpen(false);
    }
  }, [currentDashboard, siteMeta.ui.dashboards, notify, notifyError]);

  const onLayoutChange = (_currentLayout: Layout[], allLayouts: Layouts) => {
    saveToLS(
      'fractal-ems-dashboards-layouts',
      (currentDashboard as IDashboard).id,
      allLayouts,
    );
    setLayouts(allLayouts);
  };

  return (
    <Box className='home-row' sx={dashboardSx.container}>
      {isLoading ? <Spinner type='fullPage' /> : null}
      {!isLoading && currentDashboard ? (
        <>
          <Box sx={dashboardSx.header}>
            <Button
              onClick={handleResetClick}
              variant='outlined'
              color='primary'
              size='small'
              startIcon={<RestoreIcon />}
            >
              <FormattedAlias id='Reset Dashboard Layout' />
            </Button>
          </Box>
          <ResponsiveReactGridLayout
            className='layout'
            layouts={layouts}
            autoSize
            compactType='horizontal'
            draggableHandle='.drag-handle'
            containerPadding={[3, 3]}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 6, md: 6, sm: 6, xs: 6, xxs: 6 }}
            rowHeight={100}
            onLayoutChange={onLayoutChange}
          >
            {currentDashboard.cells.map((cellData) => (
              <div key={cellData.key} className='cell block-container'>
                <DashboardCell variant='points-list' data={cellData.data} />
              </div>
            ))}
          </ResponsiveReactGridLayout>
          <ResetConfirmationDialog
            open={resetDialogOpen}
            onClose={handleResetCancel}
            onConfirm={handleResetConfirm}
            dashboardName={currentDashboard?.name}
          />
        </>
      ) : null}
    </Box>
  );
}

const mapStateToProps = (state: AppDefaultStateProps) => ({
  siteMeta: state.config.siteMeta,
});

const mapDispatchToProps = {
  notify,
  notifyError,
};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboards);
